export const EMPTY_PATIENT_PERIOD_SUMMARY = {
  total: 0,
  new: 0,
  returning: 0,
  byDepartment: [],
};

/**
 * Align invoice-export patient counts with outpatient / dashboard summary.
 * New = first registration (REGISTRATION); returning = later visits (CONSULTATION).
 * Prefer invoice-type new/returning so counts match everywhere. Do not reuse the
 * 15-day billing gap (that only affects consultation amount, not new vs old).
 * Total never undercounts appointment unique patients.
 */
export function resolveAlignedPatientPeriodCounts(
  appointmentSummary = null,
  invoiceTypeSummary = null
) {
  const appt = appointmentSummary || EMPTY_PATIENT_PERIOD_SUMMARY;
  const inv = invoiceTypeSummary || EMPTY_PATIENT_PERIOD_SUMMARY;
  const apptTotal = appt.total || 0;
  const invTotal = inv.total || 0;

  if (apptTotal <= 0 && invTotal <= 0) {
    return { total: 0, new: 0, returning: 0 };
  }
  if (apptTotal <= 0) {
    return {
      total: invTotal,
      new: inv.new || 0,
      returning: inv.returning || 0,
    };
  }
  if (invTotal <= 0) {
    return {
      total: apptTotal,
      new: appt.new || 0,
      returning: appt.returning || 0,
    };
  }

  const total = Math.max(apptTotal, invTotal);
  const newCount = Math.min(inv.new || 0, total);
  return {
    total,
    new: newCount,
    returning: Math.max(0, total - newCount),
  };
}

/** True when the export period is a single calendar day. */
export function isSingleDayPeriod(
  periodStartKey = null,
  periodEndKey = null,
  periodStart = null,
  periodEnd = null
) {
  const startKey = periodStartKey ?? toPeriodDateKey(periodStart);
  const endKey = periodEndKey ?? toPeriodDateKey(periodEnd) ?? startKey;
  return Boolean(startKey && endKey && startKey === endKey);
}

/** Gap (days) after which a returning OPD patient is treated as new registration. */
export const OPD_RETURNING_VISIT_GAP_DAYS = 15;

/**
 * Existing/returning patients may take REGISTRATION again only when the last
 * REGISTRATION date (not consultation / any visit) is at least `gapDays` ago.
 * Unknown last-registration date => not eligible (CONSULTATION only).
 */
export function isRegistrationEligibleAfterVisitGap(
  lastRegistrationDate,
  gapDays = OPD_RETURNING_VISIT_GAP_DAYS
) {
  if (!lastRegistrationDate) return false;
  const gap = getDaysBetweenVisitDates(lastRegistrationDate, new Date());
  return gap != null && gap >= gapDays;
}

/**
 * Latest OPD REGISTRATION date from the patient's invoices.
 * CONSULTATION (and other invoice types) are ignored so interim consultations
 * do not reset the 15-day registration window.
 */
export function getLatestRegistrationDateFromInvoices(invoices = [], patient = null) {
  let latestDate = null;
  let latestTime = -1;

  (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
    const type = String(
      invoice?.invoiceType || invoice?.invoice_type || invoice?.indicator || ""
    )
      .trim()
      .toUpperCase();
    if (type !== "REGISTRATION") return;

    const date = normalizeDateValue(
      invoice?.date ||
        invoice?.invoiceDate ||
        invoice?.invoice_date ||
        invoice?.createdAt ||
        invoice?.created_at
    );
    if (!date) return;
    const time = date.getTime();
    if (time > latestTime) {
      latestTime = time;
      latestDate = date;
    }
  });

  if (latestDate) return latestDate;
  // Fallback: patient master registration/creation date (first registration only).
  return getRegistrationDate(patient);
}

function getOpdInvoiceAmount(invoice) {
  return (
    Number(
      invoice?.amount ??
        invoice?.subtotal ??
        invoice?.totalAmount ??
        invoice?.total_amount ??
        0
    ) || 0
  );
}

function getOpdInvoiceBillingDate(invoice) {
  return normalizeDateValue(
    invoice?.date ||
      invoice?.invoiceDate ||
      invoice?.invoice_date ||
      invoice?.createdAt ||
      invoice?.created_at
  );
}

/**
 * Latest date that anchors the 15-day consultation waiver (follow-up may be ₹0).
 * - REGISTRATION invoices always count.
 * - Paid CONSULTATION / legacy OPD invoices (amount > 0) count — a paid visit
 *   starts a new 15-day window.
 * - Free CONSULTATION (₹0 follow-up) does not extend the window.
 */
export function getLatestOpdConsultationWaiverAnchorDate(
  invoices = [],
  patient = null
) {
  let latestDate = null;
  let latestTime = -1;

  (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
    const type = String(
      invoice?.invoiceType || invoice?.invoice_type || invoice?.indicator || ""
    )
      .trim()
      .toUpperCase();

    const isRegistration = type === "REGISTRATION";
    const isPaidConsultation =
      (type === "CONSULTATION" || type === "OPD") &&
      getOpdInvoiceAmount(invoice) > 0;

    if (!isRegistration && !isPaidConsultation) return;

    const date = getOpdInvoiceBillingDate(invoice);
    if (!date) return;
    const time = date.getTime();
    if (time > latestTime) {
      latestTime = time;
      latestDate = date;
    }
  });

  if (latestDate) return latestDate;
  return getRegistrationDate(patient);
}

/** True when the patient has at least one REGISTRATION invoice (invoice table only). */
export function hasRegistrationInvoiceInList(invoices = []) {
  return (Array.isArray(invoices) ? invoices : []).some((invoice) => {
    const type = String(
      invoice?.invoiceType || invoice?.invoice_type || invoice?.indicator || ""
    )
      .trim()
      .toUpperCase();
    return type === "REGISTRATION";
  });
}

/** Latest OPD visit date from a patient's appointments list. */
export function getLatestAppointmentVisitDate(appointments = []) {
  let latestDate = null;
  let latestTime = -1;

  (appointments || []).forEach((appointment) => {
    const visitDate = getAppointmentDate(appointment);
    if (!visitDate) return;
    const visitTime = visitDate.getTime();
    if (visitTime > latestTime) {
      latestTime = visitTime;
      latestDate = visitDate;
    }
  });

  return latestDate;
}

export function parsePeriodBounds(fromDate, toDate) {
  let start = null;
  let end = null;
  if (fromDate) {
    // Prefer local calendar-day parse for YYYY-MM-DD (avoid UTC shift from `new Date("YYYY-MM-DD")`).
    start = normalizeDateValue(fromDate);
    if (start) start.setHours(0, 0, 0, 0);
  }
  if (toDate) {
    end = normalizeDateValue(toDate);
    if (end) end.setHours(23, 59, 59, 999);
  }
  return { start, end };
}

export function parseMonthYearBounds(month, year) {
  const start = new Date(year, month, 1, 0, 0, 0, 0);
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

export function getAppointmentDate(appointment) {
  if (!appointment) return null;
  const raw =
    appointment.appointmentDateTime ||
    appointment.appointment_date_time ||
    appointment.appointmentDate ||
    appointment.appointment_date ||
    appointment.date;
  if (!raw) return null;
  return normalizeDateValue(raw);
}

export function normalizeDateValue(raw) {
  if (raw == null || raw === "") return null;

  if (Array.isArray(raw)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = raw;
    if (year == null || month == null || day == null) return null;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    // MySQL / JDBC style: "2026-07-01 09:48:08.391400" (space, not ISO T)
    const dateTimeMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?/
    );
    if (dateTimeMatch) {
      const [, y, m, d, hh, mm, ss = "0"] = dateTimeMatch;
      const date = new Date(
        Number(y),
        Number(m) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss)
      );
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function toPeriodDateKey(value) {
  if (value == null || value === "") return null;

  if (Array.isArray(value)) {
    const [year, month, day] = value;
    if (year == null || month == null || day == null) return null;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    // Fast path for YYYY-MM-DD and "YYYY-MM-DD HH:mm:ss..." invoice dates
    const dayMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dayMatch) return dayMatch[1];
  }

  const normalized = normalizeDateValue(value);
  if (!normalized) return null;
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, "0");
  const day = String(normalized.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getPatientFromDetailsMap(patientDetailsMap, patientId) {
  if (patientId == null || !patientDetailsMap) return null;
  return (
    patientDetailsMap[patientId] ??
    patientDetailsMap[String(patientId)] ??
    patientDetailsMap[Number(patientId)] ??
    null
  );
}

export function getPatientRegistrationRaw(patient) {
  if (!patient) return null;
  return (
    patient.creationDate ||
    patient.registrationDate ||
    patient.registration_date ||
    patient.createdAt ||
    patient.created_at ||
    null
  );
}

export function getRegistrationDateKey(patient) {
  return toPeriodDateKey(getPatientRegistrationRaw(patient));
}

export function resolvePatientForPeriodSummary(patientId, patientDetailsMap, row = null) {
  const fromMap = getPatientFromDetailsMap(patientDetailsMap, patientId);

  const embeddedDate =
    row?.creationDate ||
    row?.registrationDate ||
    row?.registration_date ||
    row?.createdAt ||
    row?.created_at ||
    row?.patient?.creationDate ||
    row?.patient?.registrationDate;

  if (embeddedDate) {
    return {
      ...(fromMap || {}),
      id: fromMap?.id ?? patientId,
      creationDate: embeddedDate,
    };
  }

  return fromMap;
}

export function getRegistrationDate(patient) {
  return normalizeDateValue(getPatientRegistrationRaw(patient));
}

export function getAdmissionDate(admission) {
  if (!admission) return null;
  if (Array.isArray(admission.admissionDate)) {
    const [y, m, d] = admission.admissionDate;
    const [hh = 0, mm = 0, ss = 0] = admission.admissionTime || [];
    return new Date(y, (m || 1) - 1, d || 1, hh, mm, ss);
  }
  if (admission.admissionDate) {
    const parsed = new Date(admission.admissionDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (admission._date) {
    const parsed = new Date(admission._date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function isDateInRange(date, start, end) {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
}

/**
 * Calendar-day period check (same rules as invoice period keys).
 * Prefer YYYY-MM-DD keys so multi-day / monthly ranges stay correct.
 * Open-ended ranges: omit startKey and/or endKey (do not collapse to a single day).
 */
export function isVisitDateInPeriod(
  visitDate,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  const visitKey = toPeriodDateKey(visitDate);
  const startKey = periodStartKey ?? toPeriodDateKey(periodStart);
  const endKey = periodEndKey ?? toPeriodDateKey(periodEnd);

  if (visitKey && (startKey || endKey)) {
    if (startKey && visitKey < startKey) return false;
    if (endKey && visitKey > endKey) return false;
    return true;
  }

  return isDateInRange(visitDate, periodStart, periodEnd);
}

export function buildDepartmentsLookup(departments) {
  const map = {};
  (departments || []).forEach((dept) => {
    const id = dept?.id ?? dept?.departmentId;
    const name = dept?.name || dept?.departmentName;
    if (id != null) {
      map[String(id)] = name || String(id);
    }
  });
  return map;
}

export function buildDoctorsLookup(doctors) {
  const map = {};
  (doctors || []).forEach((doctor) => {
    if (doctor?.id != null) {
      map[String(doctor.id)] = doctor;
    }
    if (doctor?.userId != null) {
      map[String(doctor.userId)] = doctor;
    }
  });
  return map;
}

export function resolveDepartmentName({
  departmentId,
  departmentName,
  doctorsById,
  doctorId,
  departmentsById,
}) {
  if (departmentName) return departmentName;

  const deptKey = departmentId != null ? String(departmentId) : null;
  if (deptKey && departmentsById?.[deptKey]) {
    return departmentsById[deptKey];
  }

  const doctorKey = doctorId != null ? String(doctorId) : null;
  if (doctorKey && doctorsById?.[doctorKey]) {
    const doctor = doctorsById[doctorKey];
    if (doctor.departmentName) return doctor.departmentName;
    if (doctor.department) return doctor.department;
    if (doctor.departmentId != null) {
      const doctorDeptKey = String(doctor.departmentId);
      if (departmentsById?.[doctorDeptKey]) {
        return departmentsById[doctorDeptKey];
      }
    }
  }
  return "Unknown";
}

export function pickLatestAssignment(assignmentResponse) {
  if (!assignmentResponse) return null;
  if (!Array.isArray(assignmentResponse)) return assignmentResponse;

  if (assignmentResponse.length === 0) return null;

  const sorted = [...assignmentResponse].sort((a, b) => {
    if (a?.createdAt && b?.createdAt) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    return (b?.id || 0) - (a?.id || 0);
  });

  return sorted[0] || null;
}

export function getIpdDepartmentFromSources({ row, assignment }) {
  const nestedDepartment =
    assignment?.department?.name ||
    assignment?.department?.departmentName ||
    row?.department?.name ||
    row?.department?.departmentName;

  return {
    departmentId:
      row?.departmentId ||
      row?.department_id ||
      assignment?.departmentId ||
      assignment?.department_id ||
      assignment?.department?.id ||
      row?.department?.id,
    departmentName:
      row?.departmentName ||
      row?.department_name ||
      assignment?.departmentName ||
      assignment?.department_name ||
      nestedDepartment,
  };
}

export function getIpdAssignmentForRow(row, assignmentsByAdmissionId = {}) {
  if (!row) return null;
  return (
    row.assignment ||
    assignmentsByAdmissionId[row.id] ||
    assignmentsByAdmissionId[String(row.id)] ||
    null
  );
}

export function getGroupedAppointmentsForPatient(groupedAppointments, patientId) {
  if (!groupedAppointments || patientId == null) return [];

  const normalizedId = String(patientId);
  const direct =
    groupedAppointments[normalizedId] ||
    groupedAppointments[patientId];

  if (Array.isArray(direct) && direct.length > 0) {
    return direct;
  }

  const matches = [];
  Object.values(groupedAppointments || {}).forEach((appointments) => {
    if (!Array.isArray(appointments)) return;
    appointments.forEach((apt) => {
      const aptPatientId = apt?.patientId ?? apt?.patient_id;
      if (aptPatientId != null && String(aptPatientId) === normalizedId) {
        matches.push(apt);
      }
    });
  });

  return matches;
}

export function getFirstOpdVisitDate(patientId, groupedAppointments) {
  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, patientId);
  let firstDate = null;

  for (const apt of appointments) {
    const aptDate = getAppointmentDate(apt);
    if (!aptDate) continue;
    if (!firstDate || aptDate < firstDate) {
      firstDate = aptDate;
    }
  }

  return firstDate;
}

export function getFirstOpdVisitInPeriod(
  patientId,
  groupedAppointments,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, patientId);
  let firstDate = null;
  let firstTime = Infinity;

  for (const apt of appointments) {
    const aptDate = getAppointmentDate(apt);
    if (
      !isVisitDateInPeriod(
        aptDate,
        periodStart,
        periodEnd,
        periodStartKey,
        periodEndKey
      )
    ) {
      continue;
    }
    const visitTime = aptDate?.getTime() ?? Infinity;
    if (visitTime < firstTime) {
      firstTime = visitTime;
      firstDate = aptDate;
    }
  }

  return firstDate;
}

export function getDaysBetweenVisitDates(earlier, later) {
  const start = getStartOfDay(earlier);
  const end = getStartOfDay(later);
  if (!start || !end) return null;
  return Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

export function getLatestPriorOpdVisitDate(patientId, beforeDate, groupedAppointments) {
  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, patientId);
  const beforeDay = getStartOfDay(beforeDate);
  if (!beforeDay) return null;

  let latestDate = null;
  let latestTime = -1;

  for (const apt of appointments) {
    const aptDate = getAppointmentDate(apt);
    const visitDay = aptDate ? getStartOfDay(aptDate) : null;
    if (!visitDay || visitDay >= beforeDay) continue;
    const visitTime = visitDay.getTime();
    if (visitTime > latestTime) {
      latestTime = visitTime;
      latestDate = aptDate;
    }
  }

  return latestDate;
}

export function buildOpdVisitInPeriodPatientSet(
  groupedAppointments,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  if (!periodStart && !periodEnd && !periodStartKey && !periodEndKey) {
    return null;
  }

  const patientIds = new Set();
  Object.entries(groupedAppointments || {}).forEach(([groupKey, appointments]) => {
    if (!Array.isArray(appointments)) return;

    // Only count patients from appointments that actually fall in the period.
    // Adding every patientId from the whole group inflated monthly totals.
    const inPeriodAppointments = appointments.filter((apt) =>
      isVisitDateInPeriod(
        getAppointmentDate(apt),
        periodStart,
        periodEnd,
        periodStartKey,
        periodEndKey
      )
    );
    if (inPeriodAppointments.length === 0) return;

    let addedFromAppointments = false;
    inPeriodAppointments.forEach((apt) => {
      const aptPatientId = apt?.patientId ?? apt?.patient_id;
      if (aptPatientId != null && aptPatientId !== "") {
        patientIds.add(String(aptPatientId));
        addedFromAppointments = true;
      }
    });

    if (!addedFromAppointments && groupKey) {
      patientIds.add(String(groupKey));
    }
  });

  return patientIds;
}

export function hasOpdVisitInPeriod(
  row,
  groupedAppointments,
  periodStart,
  periodEnd,
  visitInPeriodPatientSet = null,
  periodStartKey = null,
  periodEndKey = null
) {
  if (!periodStart && !periodEnd && !periodStartKey && !periodEndKey) return true;

  if (visitInPeriodPatientSet) {
    return visitInPeriodPatientSet.has(String(row?.patientId));
  }

  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, row?.patientId);
  const candidates = appointments.length > 0 ? appointments : row ? [row] : [];

  return candidates.some((apt) =>
    isVisitDateInPeriod(
      getAppointmentDate(apt),
      periodStart,
      periodEnd,
      periodStartKey,
      periodEndKey
    )
  );
}

/** True when appointment is cancelled or rejected (excluded from monthly footfall counts). */
export function isOpdAppointmentCancelledOrRejected(appointment) {
  if (!appointment) return false;
  if (appointment.isCancel === true || appointment.is_cancel === true) return true;
  const status = String(
    appointment.status || appointment.appointmentStatus || ""
  )
    .trim()
    .toUpperCase();
  return (
    status === "CANCELLED" ||
    status === "CANCELED" ||
    status === "REJECTED"
  );
}

function appointmentHasDoctorFields(appointment) {
  const doctorId = appointment?.doctorId ?? appointment?.doctor_id;
  if (doctorId != null && doctorId !== "" && Number(doctorId) !== 0) return true;
  const doctorName = appointment?.doctorName || appointment?.doctor_name;
  return Boolean(doctorName && String(doctorName).trim());
}

export function getRepresentativeOpdAppointmentInPeriod(
  row,
  groupedAppointments,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, row?.patientId);
  let bestActive = null;
  let bestActiveTime = -1;
  let bestCancelledWithDoctor = null;
  let bestCancelledTime = -1;
  let bestAny = null;
  let bestAnyTime = -1;

  for (const apt of appointments) {
    const visitDate = getAppointmentDate(apt);
    if (
      !isVisitDateInPeriod(
        visitDate,
        periodStart,
        periodEnd,
        periodStartKey,
        periodEndKey
      )
    ) {
      continue;
    }
    const visitTime = visitDate?.getTime() || 0;
    if (visitTime > bestAnyTime) {
      bestAnyTime = visitTime;
      bestAny = apt;
    }

    if (isOpdAppointmentCancelledOrRejected(apt)) {
      // Keep cancelled visits for doctor/department display when no active visit exists.
      if (appointmentHasDoctorFields(apt) && visitTime > bestCancelledTime) {
        bestCancelledTime = visitTime;
        bestCancelledWithDoctor = apt;
      }
      continue;
    }

    if (visitTime > bestActiveTime) {
      bestActiveTime = visitTime;
      bestActive = apt;
    }
  }

  return bestActive || bestCancelledWithDoctor || bestAny || row;
}

function getStartOfDay(date) {
  const day = normalizeDateValue(date);
  if (!day) return null;
  day.setHours(0, 0, 0, 0);
  return day;
}

export function hasPriorOpdVisit(
  patientId,
  periodStart,
  groupedAppointments,
  periodStartKey = null
) {
  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, patientId);
  if (appointments.length === 0) return false;

  const startKey = periodStartKey ?? toPeriodDateKey(periodStart);
  if (startKey) {
    return appointments.some((apt) => {
      const visitKey = toPeriodDateKey(getAppointmentDate(apt));
      return visitKey && visitKey < startKey;
    });
  }

  if (!periodStart) {
    return appointments.length > 1;
  }

  const periodStartDay = getStartOfDay(periodStart);
  if (!periodStartDay) return false;

  return appointments.some((apt) => {
    const aptDate = getAppointmentDate(apt);
    const visitDay = aptDate ? getStartOfDay(aptDate) : null;
    return visitDay && visitDay < periodStartDay;
  });
}

/**
 * New = first OPD registration / first visit ever.
 * Returning = any later visit (consultation). The 15-day billing gap is not used.
 */
export function isOpdPatientNewInPeriod({
  patientId,
  patientDetailsMap,
  row = null,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null,
  groupedAppointments,
  treatAsNewRegistration = false,
}) {
  if (treatAsNewRegistration) return true;

  const startKey = periodStartKey ?? toPeriodDateKey(periodStart);
  const endKey = periodEndKey ?? toPeriodDateKey(periodEnd) ?? startKey;

  const patient = resolvePatientForPeriodSummary(patientId, patientDetailsMap, row);
  const regKey = getRegistrationDateKey(patient);

  const firstVisitInPeriod = getFirstOpdVisitInPeriod(
    patientId,
    groupedAppointments,
    periodStart,
    periodEnd,
    startKey,
    endKey
  );

  if (firstVisitInPeriod) {
    const priorVisit = getLatestPriorOpdVisitDate(
      patientId,
      firstVisitInPeriod,
      groupedAppointments
    );

    if (priorVisit) {
      // Any earlier visit means this is consultation / returning — not a new
      // registration. The 15-day gap is only for billing amount, not counts.
      return false;
    }
    return true;
  }

  if (regKey && startKey && endKey && regKey >= startKey && regKey <= endKey) {
    return true;
  }

  if (!startKey) {
    return !hasPriorOpdVisit(patientId, periodStart, groupedAppointments, null);
  }

  const hasPatientMaster = Boolean(getPatientFromDetailsMap(patientDetailsMap, patientId));
  if (!hasPatientMaster && groupedAppointments) {
    const firstVisitKey = toPeriodDateKey(getFirstOpdVisitDate(patientId, groupedAppointments));
    if (firstVisitKey) {
      return firstVisitKey >= startKey && firstVisitKey <= endKey;
    }
  }

  return !hasPriorOpdVisit(patientId, periodStart, groupedAppointments, startKey);
}

export function isOpdPatientReturning(params) {
  return !isOpdPatientNewInPeriod(params);
}

export function hasPriorIpdAdmission(patientId, periodStart, allAdmissions) {
  if (!patientId || !Array.isArray(allAdmissions)) return false;

  const patientAdmissions = allAdmissions.filter(
    (admission) => String(admission?.patientId) === String(patientId)
  );

  if (patientAdmissions.length === 0) return false;

  if (!periodStart) {
    return patientAdmissions.length > 1;
  }

  const periodStartDay = getStartOfDay(periodStart);
  if (!periodStartDay) return false;

  return patientAdmissions.some((admission) => {
    const admissionDate = getAdmissionDate(admission);
    const admissionDay = admissionDate ? getStartOfDay(admissionDate) : null;
    return admissionDay && admissionDay < periodStartDay;
  });
}

export function computePatientPeriodSummary(records) {
  const deptMap = new Map();
  const seenPatients = new Map();

  (records || []).forEach((record) => {
    const patientId = record?.patientId;
    const visitDateKey = record?.visitDateKey || null;
    const patientKey =
      patientId != null && patientId !== ""
        ? visitDateKey
          ? `id:${patientId}@${visitDateKey}`
          : `id:${patientId}`
        : (() => {
            const normalizedName = normalizePatientName(record?.patientName || "");
            if (
              !normalizedName ||
              normalizedName === "n/a" ||
              normalizedName === "null"
            ) {
              return null;
            }
            return visitDateKey
              ? `name:${normalizedName}@${visitDateKey}`
              : `name:${normalizedName}`;
          })();
    if (!patientKey) return;

    const department = record.department || "Unknown";
    if (!seenPatients.has(patientKey)) {
      seenPatients.set(patientKey, {
        isReturning: Boolean(record.isReturning),
        department,
      });
    }
  });

  let total = 0;
  let newCount = 0;
  let returningCount = 0;

  seenPatients.forEach(({ isReturning, department }) => {
    total += 1;
    if (isReturning) returningCount += 1;
    else newCount += 1;

    if (!deptMap.has(department)) {
      deptMap.set(department, { department, total: 0, new: 0, returning: 0 });
    }
    const deptStats = deptMap.get(department);
    deptStats.total += 1;
    if (isReturning) deptStats.returning += 1;
    else deptStats.new += 1;
  });

  return {
    total,
    new: newCount,
    returning: returningCount,
    byDepartment: Array.from(deptMap.values()).sort((a, b) => b.total - a.total),
  };
}

export function buildOpdPeriodSummary({
  rows,
  patientDetailsMap,
  groupedAppointments,
  doctorsById,
  departmentsById,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null,
  registrationLookup = null,
}) {
  return computePatientPeriodSummary(
    buildOpdSummaryRecords({
      rows,
      patientDetailsMap,
      groupedAppointments,
      doctorsById,
      departmentsById,
      periodStart,
      periodEnd,
      periodStartKey,
      periodEndKey,
      registrationLookup,
    })
  );
}

/** Shared OPD summary for PatientsTable + InvoiceTable (appointments + registration invoices). */
export function buildOpdPeriodSummaryForPeriod({
  groupedAppointments,
  patientDetailsMap,
  doctorsById,
  departmentsById,
  invoices = [],
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null,
  patientRows = null,
}) {
  const registrationLookup =
    periodStart || periodEnd || periodStartKey || periodEndKey
      ? buildRegistrationPatientLookup(
          invoices,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey,
          patientDetailsMap
        )
      : null;

  const singleDay = isSingleDayPeriod(
    periodStartKey,
    periodEndKey,
    periodStart,
    periodEnd
  );

  // Daily: unique patients in the day (production report).
  // Monthly/multi-day: one row per patient per visit day (footfall).
  const rows =
    patientRows ??
    (singleDay
      ? buildOpdPatientRowsInPeriod(
          groupedAppointments,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        )
      : buildOpdPatientDayRowsInPeriod(
          groupedAppointments,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        ));

  return buildOpdPeriodSummary({
    rows,
    patientDetailsMap,
    groupedAppointments,
    doctorsById,
    departmentsById,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey,
    registrationLookup,
  });
}

const resolveInvoicePatientId = (invoice) => {
  if (!invoice) return null;
  const nestedPatient =
    invoice.patient && typeof invoice.patient === "object" ? invoice.patient : null;
  const scalarPatient =
    typeof invoice.patient === "number" || typeof invoice.patient === "string"
      ? invoice.patient
      : null;

  const patientId =
    invoice.patientId ??
    invoice.patient_id ??
    invoice.patientID ??
    scalarPatient ??
    nestedPatient?.id ??
    nestedPatient?.patientId ??
    null;

  return patientId == null || patientId === "" ? null : patientId;
};

const resolveInvoicePatientIdForSummary = (invoice, patientDetailsMap = null) => {
  const directId = resolveInvoicePatientId(invoice);
  if (directId != null) return directId;
  // Only resolve by name when unique — first-match caused wrong IDs for common names.
  return resolveUniquePatientIdByName(invoice, patientDetailsMap);
};

const resolveInvoicePatientName = (invoice) =>
  invoice?.patientName ||
  invoice?.patient_name ||
  invoice?.toName ||
  invoice?.to_name ||
  "";

const buildInvoicePatientDedupeKey = (patientId, patientName) => {
  if (patientId != null && patientId !== "") {
    return `id:${patientId}`;
  }
  const normalizedName = normalizePatientName(patientName);
  return normalizedName ? `name:${normalizedName}` : null;
};

const normalizePatientName = (value) => (value || "").toString().trim().toLowerCase();

const normalizeInvoiceType = (invoice) => {
  if (!invoice) return "";
  if (invoice.admissionId || invoice.admission_id) return "DISCHARGE";
  return String(
    invoice.invoiceType || invoice.invoice_type || invoice.indicator || ""
  )
    .trim()
    .toUpperCase();
};

const isOpdInvoiceForPatientSummary = (invoice) =>
  normalizeInvoiceType(invoice) !== "DISCHARGE";

const INVOICE_PATIENT_SUMMARY_TYPES = new Set(["REGISTRATION", "CONSULTATION"]);

const resolveInvoiceSummaryType = (invoice) => {
  const type = normalizeInvoiceType(invoice);
  if (type) return type;
  const particulars = String(invoice?.particulars || "").trim().toUpperCase();
  return INVOICE_PATIENT_SUMMARY_TYPES.has(particulars) ? particulars : "";
};

const isInvoiceForPatientSummaryCount = (invoice) =>
  INVOICE_PATIENT_SUMMARY_TYPES.has(resolveInvoiceSummaryType(invoice));

const buildInvoicePatientCanonicalKeyMap = (invoices, patientDetailsMap) => {
  const nameToCanonical = new Map();
  const ambiguousNames = new Set();

  (invoices || []).forEach((invoice) => {
    if (!isInvoiceForPatientSummaryCount(invoice)) return;

    const patientName = resolveInvoicePatientName(invoice);
    const patientId = resolveInvoicePatientIdForSummary(invoice, patientDetailsMap);
    const normalizedName = normalizePatientName(patientName);
    if (!normalizedName || normalizedName === "n/a" || normalizedName === "null") return;
    if (patientId == null) return;

    const canonical = `id:${patientId}`;
    if (ambiguousNames.has(normalizedName)) return;

    const existing = nameToCanonical.get(normalizedName);
    if (existing && existing !== canonical) {
      nameToCanonical.delete(normalizedName);
      ambiguousNames.add(normalizedName);
      return;
    }

    nameToCanonical.set(normalizedName, canonical);
  });

  return nameToCanonical;
};

const resolveInvoicePatientCanonicalKey = (
  patientId,
  patientName,
  nameToCanonical = null
) => {
  const dedupeKey = buildInvoicePatientDedupeKey(patientId, patientName);
  if (!dedupeKey) return null;
  if (dedupeKey.startsWith("id:") || !nameToCanonical) return dedupeKey;

  const normalizedName = dedupeKey.slice("name:".length);
  return nameToCanonical.get(normalizedName) || dedupeKey;
};

const isInvoiceInPeriod = (
  invoice,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) => {
  const invoiceDateKey = toPeriodDateKey(
    invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
  );
  const startKey = periodStartKey ?? toPeriodDateKey(periodStart);
  const endKey = periodEndKey ?? toPeriodDateKey(periodEnd) ?? startKey;

  if (startKey && endKey && invoiceDateKey) {
    return invoiceDateKey >= startKey && invoiceDateKey <= endKey;
  }

  const invoiceDate = normalizeDateValue(
    invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
  );
  return isDateInRange(invoiceDate, periodStart, periodEnd);
};

/** Resolve patient id from invoice only when the patient name is unique in the map. */
const resolveUniquePatientIdByName = (invoice, patientDetailsMap) => {
  if (!patientDetailsMap) return null;

  const targetName = normalizePatientName(
    invoice?.patientName ||
      invoice?.patient_name ||
      invoice?.toName ||
      invoice?.to_name ||
      ""
  );
  if (!targetName || targetName === "n/a" || targetName === "null") return null;

  const matches = [];
  const seenIds = new Set();
  for (const patient of Object.values(patientDetailsMap)) {
    const id = patient?.id ?? patient?.patientId;
    if (id == null || id === "" || seenIds.has(String(id))) continue;
    seenIds.add(String(id));
    if (normalizePatientName(patient?.name) === targetName) {
      matches.push(id);
      if (matches.length > 1) return null;
    }
  }

  return matches.length === 1 ? matches[0] : null;
};

const addPatientIdToRegistrationSet = (byPatientId, patientId) => {
  if (patientId == null || patientId === "") return;
  byPatientId.add(String(patientId));
  const numericId = Number(patientId);
  if (!Number.isNaN(numericId)) {
    byPatientId.add(String(numericId));
  }
};

/** REGISTRATION invoices in period, keyed by patient id and normalized patient name. */
export function buildRegistrationPatientLookup(
  invoices,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null,
  patientDetailsMap = null
) {
  const byPatientId = new Set();
  const byPatientName = new Set();
  /** @type {Set<string>} patientId@YYYY-MM-DD */
  const byPatientIdDate = new Set();
  /** @type {Set<string>} normalizedName@YYYY-MM-DD */
  const byPatientNameDate = new Set();
  /** @type {Map<string, Set<string>>} */
  const nameToPatientIds = new Map();
  /** @type {Map<string, number>} */
  const registrationCountByName = new Map();
  const byAppointmentId = new Set();
  let matchedInvoices = 0;
  let registrationInvoices = 0;

  (invoices || []).forEach((invoice) => {
    if (!isInvoiceInPeriod(invoice, periodStart, periodEnd, periodStartKey, periodEndKey)) {
      return;
    }

    matchedInvoices += 1;
    if (normalizeInvoiceType(invoice) !== "REGISTRATION") return;

    registrationInvoices += 1;

    const patientId =
      resolveInvoicePatientId(invoice) ??
      resolveUniquePatientIdByName(invoice, patientDetailsMap);
    addPatientIdToRegistrationSet(byPatientId, patientId);

    const invoiceDateKey = toPeriodDateKey(
      invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
    );
    if (invoiceDateKey && patientId != null && patientId !== "") {
      byPatientIdDate.add(`${String(patientId)}@${invoiceDateKey}`);
      const numericId = Number(patientId);
      if (!Number.isNaN(numericId)) {
        byPatientIdDate.add(`${String(numericId)}@${invoiceDateKey}`);
      }
    }

    const patientName = normalizePatientName(
      invoice?.patientName ||
        invoice?.patient_name ||
        invoice?.toName ||
        invoice?.to_name ||
        ""
    );
    if (patientName) {
      byPatientName.add(patientName);
      if (invoiceDateKey) {
        byPatientNameDate.add(`${patientName}@${invoiceDateKey}`);
      }
      registrationCountByName.set(
        patientName,
        (registrationCountByName.get(patientName) || 0) + 1
      );
      if (patientId != null && patientId !== "") {
        if (!nameToPatientIds.has(patientName)) {
          nameToPatientIds.set(patientName, new Set());
        }
        const idSet = nameToPatientIds.get(patientName);
        idSet.add(String(patientId));
        const numericId = Number(patientId);
        if (!Number.isNaN(numericId)) {
          idSet.add(String(numericId));
        }
      }
    }

    const appointmentId = invoice?.appointmentId ?? invoice?.appointment_id;
    if (appointmentId != null && appointmentId !== "") {
      byAppointmentId.add(String(appointmentId));
      const numericAppointmentId = Number(appointmentId);
      if (!Number.isNaN(numericAppointmentId)) {
        byAppointmentId.add(String(numericAppointmentId));
      }
    }
  });

  return {
    byPatientId,
    byPatientName,
    byPatientIdDate,
    byPatientNameDate,
    nameToPatientIds,
    registrationCountByName,
    byAppointmentId,
    matchedInvoices,
    registrationInvoices,
  };
}

/** @deprecated Use buildRegistrationPatientLookup */
export function buildRegistrationPatientIdSet(
  invoices,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  return buildRegistrationPatientLookup(
    invoices,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey
  ).byPatientId;
}

const appointmentIdInRegistrationLookup = (appointmentId, registrationLookup) => {
  if (appointmentId == null || appointmentId === "") return false;
  if (!registrationLookup?.byAppointmentId?.size) return false;
  if (registrationLookup.byAppointmentId.has(String(appointmentId))) return true;
  const numericAppointmentId = Number(appointmentId);
  return (
    !Number.isNaN(numericAppointmentId) &&
    registrationLookup.byAppointmentId.has(String(numericAppointmentId))
  );
};

const patientIdInRegistrationLookup = (patientId, registrationLookup) => {
  if (patientId == null || patientId === "" || !registrationLookup?.byPatientId) return false;
  if (registrationLookup.byPatientId.has(String(patientId))) return true;
  const numericId = Number(patientId);
  return (
    !Number.isNaN(numericId) && registrationLookup.byPatientId.has(String(numericId))
  );
};

/** True when patient has a REGISTRATION invoice on the specific visit calendar day. */
const isPatientRegisteredOnVisitDate = (
  patientId,
  patientName,
  visitDateKey,
  registrationLookup
) => {
  if (!visitDateKey || !registrationLookup) return false;

  if (patientId != null && patientId !== "" && registrationLookup.byPatientIdDate?.size) {
    if (registrationLookup.byPatientIdDate.has(`${String(patientId)}@${visitDateKey}`)) {
      return true;
    }
    const numericId = Number(patientId);
    if (
      !Number.isNaN(numericId) &&
      registrationLookup.byPatientIdDate.has(`${String(numericId)}@${visitDateKey}`)
    ) {
      return true;
    }
  }

  const normalizedName = normalizePatientName(patientName || "");
  if (
    normalizedName &&
    registrationLookup.byPatientNameDate?.has(`${normalizedName}@${visitDateKey}`)
  ) {
    const idsForName = registrationLookup.nameToPatientIds?.get(normalizedName);
    if (patientId != null && patientId !== "" && idsForName && idsForName.size > 0) {
      if (idsForName.has(String(patientId))) return true;
      const numericId = Number(patientId);
      if (!Number.isNaN(numericId) && idsForName.has(String(numericId))) return true;
      return false;
    }
    return true;
  }

  return false;
};

/**
 * Name-based registration match.
 * If invoices for this name are tied to other patient IDs only, do not mark this visitor
 * as new (avoids same-name false positives). Unscoped name registrations still match.
 */
const patientNameMatchesRegistration = (patientId, patientName, registrationLookup) => {
  const normalizedName = normalizePatientName(patientName || "");
  if (!normalizedName || !registrationLookup.byPatientName.has(normalizedName)) {
    return false;
  }

  const idsForName = registrationLookup.nameToPatientIds?.get(normalizedName);
  if (patientId != null && patientId !== "" && idsForName && idsForName.size > 0) {
    if (idsForName.has(String(patientId))) return true;
    const numericId = Number(patientId);
    if (!Number.isNaN(numericId) && idsForName.has(String(numericId))) return true;
    // Name is claimed by different registered patient ID(s).
    return false;
  }

  return true;
};

export function isPatientNewRegistration(
  patientId,
  patientName,
  registrationLookup,
  row = null,
  groupedAppointments = null,
  periodStart = null,
  periodEnd = null,
  periodStartKey = null,
  periodEndKey = null
) {
  if (!registrationLookup || registrationLookup.registrationInvoices === 0) {
    return null;
  }

  if (patientIdInRegistrationLookup(patientId, registrationLookup)) {
    return true;
  }

  if (patientNameMatchesRegistration(patientId, patientName, registrationLookup)) {
    return true;
  }

  if (row && registrationLookup.byAppointmentId?.size) {
    const rowAppointmentId = row?.appointmentId ?? row?.appointment_id;
    if (appointmentIdInRegistrationLookup(rowAppointmentId, registrationLookup)) {
      return true;
    }
  }

  if (row && groupedAppointments && registrationLookup.byAppointmentId?.size) {
    const appointments = getGroupedAppointmentsForPatient(groupedAppointments, patientId);
    const candidates = appointments.length > 0 ? appointments : [row];

    for (const appointment of candidates) {
      const appointmentDate = getAppointmentDate(appointment);
      if (
        !isVisitDateInPeriod(
          appointmentDate,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        )
      ) {
        continue;
      }

      const appointmentId =
        appointment?.id ?? appointment?.appointmentId ?? appointment?.appointment_id;
      if (appointmentIdInRegistrationLookup(appointmentId, registrationLookup)) {
        return true;
      }
    }
  }

  return false;
}

/** Build OPD new/returning summary from invoices (REGISTRATION fee = new registration). */
export function buildOpdPeriodSummaryFromInvoices({
  invoices,
  patientDetailsMap,
  groupedAppointments,
  doctorsById,
  departmentsById,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null,
}) {
  const registrationLookup = buildRegistrationPatientLookup(
    invoices,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey,
    patientDetailsMap
  );
  const seenPatients = new Set();
  const rows = [];

  (invoices || []).forEach((invoice) => {
    if (!isOpdInvoiceForPatientSummary(invoice)) return;

    if (!isInvoiceInPeriod(invoice, periodStart, periodEnd, periodStartKey, periodEndKey)) {
      return;
    }

    const patientName = resolveInvoicePatientName(invoice);
    const patientId = resolveInvoicePatientIdForSummary(invoice, patientDetailsMap);
    const normalizedName = normalizePatientName(patientName);
    if (
      patientId == null &&
      (!normalizedName || normalizedName === "n/a" || normalizedName === "null")
    ) {
      return;
    }
    const dedupeKey = buildInvoicePatientDedupeKey(patientId, patientName);
    if (!dedupeKey || seenPatients.has(dedupeKey)) return;
    seenPatients.add(dedupeKey);

    rows.push({
      patientId,
      patientName,
      doctorId: invoice?.doctorId ?? invoice?.doctor_id,
      department: invoice?.department || invoice?.departmentName,
      appointmentId: invoice?.appointmentId ?? invoice?.appointment_id,
    });
  });

  return buildOpdPeriodSummary({
    rows,
    patientDetailsMap,
    groupedAppointments,
    doctorsById,
    departmentsById,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey,
    registrationLookup,
  });
}

/**
 * Invoice export patient counts: REGISTRATION + CONSULTATION.
 * Daily: unique patients (production). Monthly: patient-days (footfall).
 * New = REGISTRATION; Old/Returning = CONSULTATION without REGISTRATION (same day / period).
 */
export function buildInvoicePatientPeriodSummary({
  invoices,
  patientDetailsMap = null,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null,
}) {
  const singleDay = isSingleDayPeriod(
    periodStartKey,
    periodEndKey,
    periodStart,
    periodEnd
  );
  const nameToCanonical = buildInvoicePatientCanonicalKeyMap(invoices, patientDetailsMap);

  if (singleDay) {
    const registrationPatients = new Set();
    const consultationPatients = new Set();

    (invoices || []).forEach((invoice) => {
      if (!isInvoiceForPatientSummaryCount(invoice)) return;
      if (!isInvoiceInPeriod(invoice, periodStart, periodEnd, periodStartKey, periodEndKey)) {
        return;
      }

      const patientName = resolveInvoicePatientName(invoice);
      const patientId = resolveInvoicePatientIdForSummary(invoice, patientDetailsMap);
      const normalizedName = normalizePatientName(patientName);
      if (
        patientId == null &&
        (!normalizedName || normalizedName === "n/a" || normalizedName === "null")
      ) {
        return;
      }

      const dedupeKey = resolveInvoicePatientCanonicalKey(
        patientId,
        patientName,
        nameToCanonical
      );
      if (!dedupeKey) return;

      const invoiceType = resolveInvoiceSummaryType(invoice);
      if (invoiceType === "REGISTRATION") {
        registrationPatients.add(dedupeKey);
        return;
      }
      if (invoiceType === "CONSULTATION") {
        consultationPatients.add(dedupeKey);
      }
    });

    const newCount = registrationPatients.size;
    let returningCount = 0;
    consultationPatients.forEach((key) => {
      if (!registrationPatients.has(key)) {
        returningCount += 1;
      }
    });

    return {
      total: newCount + returningCount,
      new: newCount,
      returning: returningCount,
    };
  }

  /** @type {Map<string, boolean>} dayKey -> isNew */
  const patientDays = new Map();

  (invoices || []).forEach((invoice) => {
    if (!isInvoiceForPatientSummaryCount(invoice)) return;
    if (!isInvoiceInPeriod(invoice, periodStart, periodEnd, periodStartKey, periodEndKey)) {
      return;
    }

    const invoiceDateKey = toPeriodDateKey(
      invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
    );
    if (!invoiceDateKey) return;

    const patientName = resolveInvoicePatientName(invoice);
    const patientId = resolveInvoicePatientIdForSummary(invoice, patientDetailsMap);
    const normalizedName = normalizePatientName(patientName);
    if (
      patientId == null &&
      (!normalizedName || normalizedName === "n/a" || normalizedName === "null")
    ) {
      return;
    }

    const dedupeKey = resolveInvoicePatientCanonicalKey(
      patientId,
      patientName,
      nameToCanonical
    );
    if (!dedupeKey) return;

    const dayKey = `${dedupeKey}@${invoiceDateKey}`;
    const invoiceType = resolveInvoiceSummaryType(invoice);
    if (invoiceType === "REGISTRATION") {
      patientDays.set(dayKey, true);
      return;
    }
    if (invoiceType === "CONSULTATION") {
      if (!patientDays.has(dayKey)) {
        patientDays.set(dayKey, false);
      }
    }
  });

  let newCount = 0;
  let returningCount = 0;
  patientDays.forEach((isNew) => {
    if (isNew) newCount += 1;
    else returningCount += 1;
  });

  return {
    total: newCount + returningCount,
    new: newCount,
    returning: returningCount,
  };
}

/**
 * Monthly Excel table rows.
 * Base = same REG/CONSULT invoice patient-days as Overall Summary (restores row count).
 * Assigned Doctor filled from same-day appointment for that patientId only
 * (never remaps invoice patient_id — keeps Amit/3454).
 * Daily export must not use this path.
 */
export function buildMonthlyOpdExcelTableRows({
  invoices = [],
  groupedAppointments = {},
  patientDetailsMap = null,
  doctorsById = {},
  periodStart = null,
  periodEnd = null,
  periodStartKey = null,
  periodEndKey = null,
}) {
  const invoiceDayRows = buildInvoiceOpdPatientDayRows({
    invoices,
    patientDetailsMap,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey,
  });

  const appointmentDayRows = buildOpdPatientDayRowsInPeriod(
    groupedAppointments,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey
  );

  // Display-only: include cancelled/rejected so billed cancelled visits still show doctor.
  const doctorLookupByPatientDate = buildAppointmentDoctorLookupByPatientDate(
    groupedAppointments,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey
  );

  /** @type {Map<string, object>} */
  const appointmentByPatientDate = new Map();
  (appointmentDayRows || []).forEach((apt) => {
    const patientId = apt?.patientId ?? apt?.patient_id;
    const visitDateKey =
      apt?.visitDateKey || toPeriodDateKey(getAppointmentDate(apt));
    if (patientId == null || patientId === "" || !visitDateKey) return;
    appointmentByPatientDate.set(`${String(patientId)}@${visitDateKey}`, apt);
  });

  const resolveDoctorName = (doctorId, doctorName) => {
    if (doctorName && String(doctorName).trim()) return String(doctorName).trim();
    if (doctorId == null || doctorId === "" || Number(doctorId) === 0) return "";
    const doctor =
      doctorsById?.[doctorId] ||
      doctorsById?.[String(doctorId)] ||
      doctorsById?.[Number(doctorId)];
    return doctor?.name ? String(doctor.name).trim() : "";
  };

  /** @type {Map<string, object>} */
  const rowMap = new Map();

  const rowKeyFor = (patientId, patientName, visitDateKey) => {
    if (patientId != null && patientId !== "") {
      return `id:${String(patientId)}@${visitDateKey}`;
    }
    const normalizedName = normalizePatientName(patientName);
    return normalizedName ? `name:${normalizedName}@${visitDateKey}` : null;
  };

  // 1) Invoice footfall rows (same set that drives summary totals)
  (invoiceDayRows || []).forEach((row) => {
    const visitDateKey = row?.visitDateKey || "";
    const patientId =
      row?.patientId ??
      row?.patient_id ??
      row?.rawInvoicePatientId ??
      null;
    // Excel needs a real patient_id to load master details (email/mobile/gender/age).
    // Name-only invoice rows produce sparse "name + visit date" sheets — skip them here.
    if (patientId == null || patientId === "" || !visitDateKey) return;

    const patientName =
      String(row?.patientName || "").trim() ||
      String(getPatientFromDetailsMap(patientDetailsMap, patientId)?.name || "").trim() ||
      `Patient ${patientId}`;
    const key = rowKeyFor(patientId, patientName, visitDateKey);
    if (!key) return;

    const patientDateKey = `${String(patientId)}@${visitDateKey}`;
    const apt =
      appointmentByPatientDate.get(patientDateKey) ||
      doctorLookupByPatientDate.get(patientDateKey) ||
      null;
    const doctorId = apt?.doctorId ?? apt?.doctor_id ?? row?.doctorId ?? row?.doctor_id ?? null;
    const doctorName =
      resolveDoctorName(apt?.doctorId ?? apt?.doctor_id, apt?.doctorName || apt?.doctor_name) ||
      resolveDoctorName(row?.doctorId ?? row?.doctor_id, row?.doctorName || row?.doctor_name) ||
      "";

    rowMap.set(key, {
      ...(apt || {}),
      ...row,
      patientId,
      patientName,
      visitDateKey,
      doctorId,
      doctorName,
    });
  });

  // 2) Appointment-only days not already present from invoices
  (appointmentDayRows || []).forEach((apt) => {
    const patientId = apt?.patientId ?? apt?.patient_id;
    const visitDateKey =
      apt?.visitDateKey || toPeriodDateKey(getAppointmentDate(apt)) || "";
    const patientName =
      String(getPatientFromDetailsMap(patientDetailsMap, patientId)?.name || "").trim() ||
      String(apt?.patientName || apt?.patient_name || "").trim() ||
      (patientId != null ? `Patient ${patientId}` : "");
    const key = rowKeyFor(patientId, patientName, visitDateKey);
    if (!key || rowMap.has(key)) return;

    const doctorId = apt?.doctorId ?? apt?.doctor_id ?? null;
    rowMap.set(key, {
      ...apt,
      patientId,
      patientName,
      visitDateKey,
      doctorId,
      doctorName: resolveDoctorName(doctorId, apt?.doctorName || apt?.doctor_name),
    });
  });

  return Array.from(rowMap.values()).sort((a, b) => {
    const dateCmp = String(a.visitDateKey || "").localeCompare(String(b.visitDateKey || ""));
    if (dateCmp !== 0) return dateCmp;
    return Number(b.patientId) - Number(a.patientId);
  });
}

/**
 * One row per REGISTRATION/CONSULTATION patient-day (billing OPD attendance).
 * Same patient with REG + CONSULT on the same day counts once.
 */
export function buildInvoiceOpdPatientDayRows({
  invoices = [],
  patientDetailsMap = null,
  periodStart = null,
  periodEnd = null,
  periodStartKey = null,
  periodEndKey = null,
}) {
  /** @type {Map<string, object>} */
  const dayMap = new Map();
  const nameToCanonical = buildInvoicePatientCanonicalKeyMap(invoices, patientDetailsMap);

  (invoices || []).forEach((invoice) => {
    if (!isInvoiceForPatientSummaryCount(invoice)) return;
    if (!isInvoiceInPeriod(invoice, periodStart, periodEnd, periodStartKey, periodEndKey)) {
      return;
    }

    const visitDateKey = toPeriodDateKey(
      invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
    );
    if (!visitDateKey) return;

    const patientNameRaw = resolveInvoicePatientName(invoice);
    // Always prefer invoice.patient_id from DB (e.g. Amit=3454). Name fallback only if id missing.
    const directPatientId = resolveInvoicePatientId(invoice);
    const patientId =
      directPatientId != null && directPatientId !== ""
        ? directPatientId
        : resolveUniquePatientIdByName(invoice, patientDetailsMap);
    const patientName =
      patientNameRaw ||
      getPatientFromDetailsMap(patientDetailsMap, patientId)?.name ||
      "";
    const normalizedName = normalizePatientName(patientName);
    if (
      patientId == null &&
      (!normalizedName || normalizedName === "n/a" || normalizedName === "null")
    ) {
      return;
    }

    // When we have a real patient_id, key by id only — never merge into another same-name id
    const dayKey =
      patientId != null && patientId !== ""
        ? `${String(patientId)}@${visitDateKey}`
        : (() => {
            const dedupeKey = resolveInvoicePatientCanonicalKey(
              patientId,
              patientName,
              nameToCanonical
            );
            return dedupeKey ? `${dedupeKey}@${visitDateKey}` : null;
          })();
    if (!dayKey) return;

    const invoiceType = resolveInvoiceSummaryType(invoice);
    const existing = dayMap.get(dayKey);
    // Prefer REGISTRATION row metadata when both exist on the same day.
    if (existing && existing.invoiceType === "REGISTRATION") return;
    if (existing && invoiceType !== "REGISTRATION") return;

    dayMap.set(dayKey, {
      patientId: patientId ?? null,
      patientName: patientName || "",
      visitDateKey,
      doctorId: invoice?.doctorId ?? invoice?.doctor_id ?? null,
      doctorName: invoice?.doctorName ?? invoice?.doctor_name ?? null,
      department: invoice?.department || invoice?.departmentName || null,
      appointmentId:
        invoice?.appointmentId ?? invoice?.appointment_id ?? null,
      rawInvoicePatientId: directPatientId,
      isInvoiceOnlyRow: true,
      invoiceType,
      appointmentDateTime: visitDateKey,
    });
  });

  return Array.from(dayMap.values());
}

/**
 * Pick best appointment among same-name same-day candidates.
 * Prefers appointmentId match, then master-name match, then highest patient id
 * (newer registrations — fixes Amit 660 → 3454 style mismatches).
 */
function pickBestAppointmentCandidate(
  candidates = [],
  {
    appointmentId = null,
    invoiceName = "",
    preferredPatientId = null,
    patientDetailsMap = null,
  } = {}
) {
  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0];

  if (appointmentId != null && appointmentId !== "") {
    const byAptId = candidates.find((apt) => {
      const id = apt?.id ?? apt?.appointmentId ?? apt?.appointment_id;
      return id != null && String(id) === String(appointmentId);
    });
    if (byAptId) return byAptId;
  }

  if (preferredPatientId != null && preferredPatientId !== "") {
    const byId = candidates.find(
      (apt) => String(apt?.patientId ?? apt?.patient_id) === String(preferredPatientId)
    );
    if (byId) return byId;
  }

  const nameMatched = invoiceName
    ? candidates.filter((apt) => {
        const aptId = apt?.patientId ?? apt?.patient_id;
        const masterName = normalizePatientName(
          getPatientFromDetailsMap(patientDetailsMap, aptId)?.name
        );
        const aptName = normalizePatientName(apt?.patientName || apt?.patient_name || "");
        return masterName === invoiceName || aptName === invoiceName;
      })
    : candidates;

  const pool = nameMatched.length ? nameMatched : candidates;
  if (pool.length === 1) return pool[0];

  // Prefer newest / highest patient id when the same name appears multiple times.
  return pool.reduce((best, apt) => {
    const bestId = Number(best?.patientId ?? best?.patient_id) || 0;
    const nextId = Number(apt?.patientId ?? apt?.patient_id) || 0;
    return nextId >= bestId ? apt : best;
  }, pool[0]);
}

/**
 * Enrich monthly invoice export rows with appointment patientId + assigned doctor.
 * Invoices often lack doctor and may carry a mismatched patientId for common names.
 * Summary counts stay invoice-based; only row display fields are corrected.
 */
function enrichInvoiceExportRowsWithAppointments(
  invoiceRows = [],
  appointmentRows = [],
  patientDetailsMap = null
) {
  /** @type {Map<string, object>} */
  const apptByIdDate = new Map();
  /** @type {Map<string, object>} */
  const apptByAppointmentId = new Map();
  /** @type {Map<string, object[]>} */
  const apptByNameDate = new Map();

  (appointmentRows || []).forEach((apt) => {
    const patientId = apt?.patientId ?? apt?.patient_id;
    const visitDateKey =
      apt?.visitDateKey || toPeriodDateKey(getAppointmentDate(apt)) || null;
    if (!visitDateKey) return;

    if (patientId != null && patientId !== "") {
      apptByIdDate.set(`${String(patientId)}@${visitDateKey}`, apt);
    }

    const aptId = apt?.id ?? apt?.appointmentId ?? apt?.appointment_id;
    if (aptId != null && aptId !== "") {
      apptByAppointmentId.set(String(aptId), apt);
    }

    const nameFromMaster = normalizePatientName(
      getPatientFromDetailsMap(patientDetailsMap, patientId)?.name
    );
    const nameFromApt = normalizePatientName(
      apt?.patientName || apt?.patient_name || ""
    );
    const normalizedName = nameFromApt || nameFromMaster;
    if (!normalizedName || normalizedName === "n/a" || normalizedName === "null") {
      return;
    }
    const nameKey = `${normalizedName}@${visitDateKey}`;
    if (!apptByNameDate.has(nameKey)) apptByNameDate.set(nameKey, []);
    apptByNameDate.get(nameKey).push(apt);
  });

  const pickDoctorFields = (apt, fallback = {}) => {
    const doctorId =
      apt?.doctorId ??
      apt?.doctor_id ??
      fallback.doctorId ??
      fallback.doctor_id ??
      null;
    const doctorName =
      apt?.doctorName ||
      apt?.doctor_name ||
      fallback.doctorName ||
      fallback.doctor_name ||
      null;
    return { doctorId, doctorName };
  };

  const resolveDisplayName = (row, patientId, matchedApt = null) => {
    const fromRow = String(row?.patientName || "").trim();
    if (fromRow) return fromRow;
    const fromApt = String(matchedApt?.patientName || matchedApt?.patient_name || "").trim();
    if (fromApt) return fromApt;
    const fromMaster = String(
      getPatientFromDetailsMap(patientDetailsMap, patientId)?.name || ""
    ).trim();
    return fromMaster;
  };

  const enriched = (invoiceRows || []).map((row) => {
    const visitDateKey = row?.visitDateKey || null;
    const invoiceName = normalizePatientName(row?.patientName || "");
    let patientId = row?.patientId ?? row?.patient_id ?? null;
    const invoicePatientIdTrusted =
      patientId != null &&
      patientId !== "" &&
      (!invoiceName ||
        !patientDetailsMap ||
        (() => {
          const masterName = normalizePatientName(
            getPatientFromDetailsMap(patientDetailsMap, patientId)?.name
          );
          return !masterName || masterName === invoiceName;
        })());

    // If invoice patientId points at a different person than the invoice name, do not
    // trust id-based appointment lookup (that produced blank/wrong detail rows).
    if (patientId != null && patientId !== "" && invoiceName) {
      const masterName = normalizePatientName(
        getPatientFromDetailsMap(patientDetailsMap, patientId)?.name
      );
      if (masterName && masterName !== invoiceName) {
        patientId = null;
      }
    }

    let matchedApt = null;

    // 1) Direct appointment id on the invoice
    const rowAppointmentId = row?.appointmentId ?? row?.appointment_id;
    if (rowAppointmentId != null && rowAppointmentId !== "") {
      matchedApt = apptByAppointmentId.get(String(rowAppointmentId)) || null;
    }

    // 2) Trusted patientId + visit date
    if (!matchedApt && invoicePatientIdTrusted && patientId != null && visitDateKey) {
      matchedApt = apptByIdDate.get(`${String(patientId)}@${visitDateKey}`) || null;
    }

    // 3) Same-name same-day — ONLY when invoice patientId is missing/untrusted.
    // Never remap a trusted id (e.g. Amit 3454) onto another same-name appointment.
    if (
      !matchedApt &&
      !invoicePatientIdTrusted &&
      invoiceName &&
      visitDateKey
    ) {
      const candidates = apptByNameDate.get(`${invoiceName}@${visitDateKey}`) || [];
      matchedApt = pickBestAppointmentCandidate(candidates, {
        appointmentId: rowAppointmentId,
        invoiceName,
        preferredPatientId: null,
        patientDetailsMap,
      });
    }

    if (matchedApt) {
      const aptPatientId = matchedApt.patientId ?? matchedApt.patient_id ?? null;
      // Keep trusted invoice patient id even if a same-day appointment was linked for doctor only
      const resolvedPatientId = invoicePatientIdTrusted
        ? patientId
        : aptPatientId ?? patientId;
      const samePatient =
        aptPatientId == null ||
        resolvedPatientId == null ||
        String(aptPatientId) === String(resolvedPatientId);
      const { doctorId, doctorName } = samePatient
        ? pickDoctorFields(matchedApt, row)
        : pickDoctorFields(row, {});
      const displayName = resolveDisplayName(row, resolvedPatientId, samePatient ? matchedApt : null);
      return {
        ...(samePatient ? matchedApt : {}),
        ...row,
        patientId: resolvedPatientId,
        patientName: displayName,
        visitDateKey: visitDateKey || matchedApt.visitDateKey,
        doctorId,
        doctorName,
        isInvoiceOnlyRow: !samePatient,
      };
    }

    // No appointment match — keep invoice identity (registration-only visits like Amit 3454).
    const { doctorId, doctorName } = pickDoctorFields(row);
    const fallbackId = invoicePatientIdTrusted
      ? row?.patientId ?? row?.patient_id ?? patientId
      : patientId;
    return {
      ...row,
      patientId: fallbackId,
      patientName: resolveDisplayName(row, fallbackId),
      doctorId,
      doctorName,
    };
  });

  // Deduplicate after ID corrections (wrong id + correct id can become the same day).
  /** @type {Map<string, object>} */
  const deduped = new Map();
  enriched.forEach((row) => {
    const visitDateKey = row?.visitDateKey || "";
    const patientId = row?.patientId ?? row?.patient_id;
    const normalizedName = normalizePatientName(row?.patientName || "");
    const key =
      patientId != null && patientId !== ""
        ? `id:${patientId}@${visitDateKey}`
        : normalizedName
          ? `name:${normalizedName}@${visitDateKey}`
          : null;
    if (!key) return;

    const existing = deduped.get(key);
    if (!existing) {
      deduped.set(key, row);
      return;
    }

    const score = (r) => {
      let s = 0;
      if (r?.patientId != null && r?.patientId !== "") s += 4;
      if (String(r?.patientName || "").trim()) s += 2;
      if (r?.doctorId || r?.doctor_id || r?.doctorName || r?.doctor_name) s += 2;
      if (r?.invoiceType === "REGISTRATION") s += 1;
      return s;
    };

    if (score(row) >= score(existing)) {
      deduped.set(key, {
        ...existing,
        ...row,
        doctorId: row.doctorId || row.doctor_id || existing.doctorId || existing.doctor_id,
        doctorName:
          row.doctorName ||
          row.doctor_name ||
          existing.doctorName ||
          existing.doctor_name,
        patientName: row.patientName || existing.patientName,
        patientId: row.patientId ?? existing.patientId,
      });
    }
  });

  return Array.from(deduped.values());
}

/**
 * Monthly Excel body: invoice patient-days (incl. registration-only) + appointments.
 * Invoices with a real patient_id are always kept (e.g. Amit 3454 with no appointment).
 * Appointments overlay doctor/details and add visit-only days not on invoices.
 * Overall Summary totals remain invoice-based (caller keeps invoice summary).
 */
function buildMonthlyExportDisplayRows(
  invoiceDayRows = [],
  appointmentDayRows = [],
  patientDetailsMap = null,
  doctorLookupByPatientDate = null
) {
  const enrichedInvoices = enrichInvoiceExportRowsWithAppointments(
    invoiceDayRows,
    appointmentDayRows,
    patientDetailsMap
  );

  /** @type {Map<string, object>} */
  const byIdDate = new Map();

  const resolveName = (row, patientId) => {
    const fromRow = String(row?.patientName || row?.patient_name || "").trim();
    if (fromRow) return fromRow;
    return String(
      getPatientFromDetailsMap(patientDetailsMap, patientId)?.name || ""
    ).trim();
  };

  const upsertByIdDate = (row, { preferIncomingDoctor = false } = {}) => {
    const visitDateKey =
      row?.visitDateKey || toPeriodDateKey(getAppointmentDate(row)) || "";
    let patientId = row?.patientId ?? row?.patient_id ?? null;
    if (
      (patientId == null || patientId === "") &&
      row?.rawInvoicePatientId != null &&
      row?.rawInvoicePatientId !== ""
    ) {
      patientId = row.rawInvoicePatientId;
    }
    const patientName = resolveName(row, patientId);
    if (patientId == null || patientId === "" || !visitDateKey) return false;

    const idKey = `${String(patientId)}@${visitDateKey}`;
    const next = {
      ...row,
      patientId,
      patientName: patientName || `Patient ${patientId}`,
      visitDateKey,
    };
    const existing = byIdDate.get(idKey);
    if (!existing) {
      byIdDate.set(idKey, next);
      return true;
    }

    const existingHasDoctor = Boolean(
      existing.doctorId || existing.doctor_id || existing.doctorName || existing.doctor_name
    );
    const nextHasDoctor = Boolean(
      next.doctorId || next.doctor_id || next.doctorName || next.doctor_name
    );
    const shouldReplaceDoctor =
      preferIncomingDoctor || (!existingHasDoctor && nextHasDoctor);

    byIdDate.set(idKey, {
      ...existing,
      ...next,
      patientId,
      patientName: next.patientName || existing.patientName,
      visitDateKey,
      doctorId: shouldReplaceDoctor
        ? next.doctorId ?? next.doctor_id ?? existing.doctorId ?? existing.doctor_id
        : existing.doctorId ?? existing.doctor_id ?? next.doctorId ?? next.doctor_id,
      doctorName: shouldReplaceDoctor
        ? next.doctorName || next.doctor_name || existing.doctorName || existing.doctor_name
        : existing.doctorName || existing.doctor_name || next.doctorName || next.doctor_name,
    });
    return true;
  };

  // 1) Seed from invoices first — keeps registration-only patients (Amit 3454)
  (enrichedInvoices || []).forEach((row) => {
    upsertByIdDate(row, { preferIncomingDoctor: false });
  });

  // Also seed raw invoice day rows in case enrichment dropped a trusted id
  (invoiceDayRows || []).forEach((row) => {
    const patientId = row?.patientId ?? row?.patient_id ?? row?.rawInvoicePatientId;
    const visitDateKey = row?.visitDateKey || "";
    if (patientId == null || patientId === "" || !visitDateKey) return;
    if (byIdDate.has(`${String(patientId)}@${visitDateKey}`)) return;
    upsertByIdDate({
      ...row,
      patientId,
      patientName: resolveName(row, patientId),
      visitDateKey,
    });
  });

  // 2) Overlay appointments (doctor + appointment-only visits)
  (appointmentDayRows || []).forEach((apt) => {
    const patientId = apt?.patientId ?? apt?.patient_id ?? null;
    const visitDateKey =
      apt?.visitDateKey || toPeriodDateKey(getAppointmentDate(apt)) || null;
    if (!visitDateKey || patientId == null || patientId === "") return;
    upsertByIdDate(
      {
        ...apt,
        patientId,
        patientName: resolveName(apt, patientId),
        visitDateKey,
      },
      { preferIncomingDoctor: true }
    );
  });

  // 3) Display-only: fill doctor from cancelled/rejected same-day appointments
  // without adding cancelled-only visits as new footfall rows.
  return applyAppointmentDoctorLookupToRows(
    Array.from(byIdDate.values()),
    doctorLookupByPatientDate
  );
}

/**
 * Build OPD export patient rows + summary.
 * Daily: production path (unique appointment patients + registration lookup).
 * Monthly: REG/CONSULT invoice patient-days when available (footfall), else appointments.
 */
export function buildAlignedOpdPeriodExport({
  groupedAppointments,
  invoices = [],
  patientDetailsMap = null,
  doctorsById = {},
  departmentsById = {},
  periodStart = null,
  periodEnd = null,
  periodStartKey = null,
  periodEndKey = null,
}) {
  const singleDay = isSingleDayPeriod(
    periodStartKey,
    periodEndKey,
    periodStart,
    periodEnd
  );

  // Daily: do not switch to invoice-only rows (breaks dept breakdown + new/old).
  if (singleDay) {
    const rows = buildOpdPatientRowsInPeriod(
      groupedAppointments,
      periodStart,
      periodEnd,
      periodStartKey,
      periodEndKey
    );
    // Display-only: fill doctor from cancelled/rejected same-day appointments.
    const doctorLookupByPatientDate = buildAppointmentDoctorLookupByPatientDate(
      groupedAppointments,
      periodStart,
      periodEnd,
      periodStartKey,
      periodEndKey
    );
    const rowsWithDoctor = applyAppointmentDoctorLookupToRows(
      (rows || []).map((row) => ({
        ...row,
        visitDateKey:
          row?.visitDateKey ||
          periodStartKey ||
          toPeriodDateKey(getAppointmentDate(row)) ||
          toPeriodDateKey(periodStart) ||
          "",
      })),
      doctorLookupByPatientDate
    );
    const summary = buildOpdPeriodSummaryForPeriod({
      groupedAppointments,
      patientDetailsMap,
      doctorsById,
      departmentsById,
      invoices,
      periodStart,
      periodEnd,
      periodStartKey,
      periodEndKey,
      patientRows: rowsWithDoctor,
    });
    return { rows: rowsWithDoctor, summary };
  }

  const appointmentDayRows = buildOpdPatientDayRowsInPeriod(
    groupedAppointments,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey
  );
  const invoiceDayRows = buildInvoiceOpdPatientDayRows({
    invoices,
    patientDetailsMap,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey,
  });
  const doctorLookupByPatientDate = buildAppointmentDoctorLookupByPatientDate(
    groupedAppointments,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey
  );

  const registrationLookup =
    periodStart || periodEnd || periodStartKey || periodEndKey
      ? buildRegistrationPatientLookup(
          invoices,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey,
          patientDetailsMap
        )
      : null;

  const summarize = (rows) =>
    buildOpdPeriodSummary({
      rows,
      patientDetailsMap,
      groupedAppointments,
      doctorsById,
      departmentsById,
      periodStart,
      periodEnd,
      periodStartKey,
      periodEndKey,
      registrationLookup,
    });

  const appointmentSummary = summarize(appointmentDayRows);
  const invoiceSummary = summarize(invoiceDayRows);
  const apptTotal = appointmentSummary.total || 0;
  const invTotal = invoiceSummary.total || 0;

  // Always merge appointments + invoice patient-days for the Excel body.
  // Registration-only invoices (e.g. Amit patient_id=3454, appointment_id null)
  // are missing if we export appointments alone when apptTotal > invTotal.
  const baseRows = buildMonthlyExportDisplayRows(
    invoiceDayRows,
    appointmentDayRows,
    patientDetailsMap,
    doctorLookupByPatientDate
  );
  const rows = baseRows.slice().sort((a, b) => {
    const dateCmp = String(a.visitDateKey || "").localeCompare(String(b.visitDateKey || ""));
    if (dateCmp !== 0) return dateCmp;
    return Number(b.patientId) - Number(a.patientId);
  });
  // Overall Summary: prefer invoice footfall when it covers the period.
  const useInvoiceSummary = invTotal > 0 && invTotal >= apptTotal;
  const summary = useInvoiceSummary ? invoiceSummary : appointmentSummary;

  return { rows, summary };
}

export function buildOpdSummaryRecords({
  rows,
  patientDetailsMap,
  groupedAppointments,
  doctorsById,
  departmentsById,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null,
  registrationLookup = null,
}) {
  const singleDay = isSingleDayPeriod(
    periodStartKey,
    periodEndKey,
    periodStart,
    periodEnd
  );

  const records = (rows || []).map((row) => {
    const patientId = row.patientId ?? row.patient_id;
    const patientName =
      row.patientName ||
      row.patient_name ||
      getPatientFromDetailsMap(patientDetailsMap, patientId)?.name ||
      "";

    // Daily: ignore visitDateKey so counts stay unique-patient (production).
    // Monthly: keep visit day for footfall / per-day new-old.
    const visitDateKey = singleDay
      ? null
      : row.visitDateKey || toPeriodDateKey(getAppointmentDate(row)) || null;

    const rowPeriodStartKey = singleDay
      ? periodStartKey
      : visitDateKey || periodStartKey;
    const rowPeriodEndKey = singleDay
      ? periodEndKey
      : visitDateKey || periodEndKey;
    const rowPeriodStart = singleDay
      ? periodStart
      : visitDateKey
        ? normalizeDateValue(visitDateKey)
        : periodStart;
    const rowPeriodEnd = singleDay
      ? periodEnd
      : visitDateKey
        ? (() => {
            const end = normalizeDateValue(visitDateKey);
            if (end) end.setHours(23, 59, 59, 999);
            return end;
          })()
        : periodEnd;

    const representativeAppointment = getRepresentativeOpdAppointmentInPeriod(
      { ...row, patientId },
      groupedAppointments,
      rowPeriodStart,
      rowPeriodEnd,
      rowPeriodStartKey,
      rowPeriodEndKey
    );

    // Daily: doctor-based department only (production dept breakdown).
    const department = resolveDepartmentName({
      departmentId: singleDay
        ? null
        : row.departmentId ||
          row.department_id ||
          representativeAppointment?.departmentId ||
          null,
      departmentName: singleDay
        ? null
        : row.department ||
          row.departmentName ||
          representativeAppointment?.department ||
          representativeAppointment?.departmentName ||
          null,
      doctorsById,
      doctorId: representativeAppointment?.doctorId || row.doctorId,
      departmentsById,
    });

    let isNewRegistration = null;
    if (registrationLookup && registrationLookup.registrationInvoices > 0) {
      if (singleDay) {
        // Production daily: period-wide registration match.
        isNewRegistration = isPatientNewRegistration(
          patientId,
          patientName,
          registrationLookup,
          row,
          groupedAppointments,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        );
      } else if (visitDateKey) {
        // Monthly: only force "new" for same-day REGISTRATION; else first visit ever.
        if (
          isPatientRegisteredOnVisitDate(
            patientId,
            patientName,
            visitDateKey,
            registrationLookup
          )
        ) {
          isNewRegistration = true;
        }
      } else {
        isNewRegistration = isPatientNewRegistration(
          patientId,
          patientName,
          registrationLookup,
          row,
          groupedAppointments,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        );
      }
    }

    const isReturning =
      isNewRegistration === null
        ? isOpdPatientReturning({
            patientId,
            patientDetailsMap,
            row,
            periodStart: rowPeriodStart,
            periodEnd: rowPeriodEnd,
            periodStartKey: rowPeriodStartKey,
            periodEndKey: rowPeriodEndKey,
            groupedAppointments,
          })
        : !isNewRegistration;

    const matchedByPatientId =
      !singleDay && visitDateKey
        ? isPatientRegisteredOnVisitDate(
            patientId,
            patientName,
            visitDateKey,
            registrationLookup
          )
        : patientIdInRegistrationLookup(patientId, registrationLookup);
    const regKey = getRegistrationDateKey(
      resolvePatientForPeriodSummary(patientId, patientDetailsMap, row)
    );
    const startKey = periodStartKey ?? toPeriodDateKey(periodStart);
    const endKey = periodEndKey ?? toPeriodDateKey(periodEnd) ?? startKey;
    const createdInPeriod = Boolean(
      regKey && startKey && endKey && regKey >= startKey && regKey <= endKey
    );

    return {
      patientId,
      patientName,
      department,
      isReturning,
      matchedByPatientId,
      createdInPeriod,
      ...(visitDateKey ? { visitDateKey } : {}),
    };
  });

  return disambiguateNewPatientsSharingRegistrationName(records, registrationLookup);
}

/**
 * When several visitors share a name but fewer REGISTRATION invoices exist for that name,
 * keep the strongest "new" candidates (id match / created in period) and demote the rest.
 */
function disambiguateNewPatientsSharingRegistrationName(records, registrationLookup) {
  if (!registrationLookup?.registrationInvoices) return records;

  const newByName = new Map();
  records.forEach((record, index) => {
    if (record.isReturning) return;
    const name = normalizePatientName(record.patientName || "");
    if (!name) return;
    if (!newByName.has(name)) newByName.set(name, []);
    newByName.get(name).push(index);
  });

  newByName.forEach((indexes, name) => {
    if (indexes.length <= 1) return;

    const allowed =
      registrationLookup.registrationCountByName?.get(name) ||
      (registrationLookup.byPatientName?.has(name) ? 1 : indexes.length);
    if (indexes.length <= allowed) return;

    const ranked = indexes
      .map((index) => ({ index, record: records[index] }))
      .sort((a, b) => {
        const score = (item) =>
          (item.record.matchedByPatientId ? 2 : 0) + (item.record.createdInPeriod ? 1 : 0);
        return score(b) - score(a);
      });

    ranked.slice(allowed).forEach(({ index }) => {
      records[index] = { ...records[index], isReturning: true };
    });
  });

  return records;
}

export function buildIpdSummaryRecords({
  rows,
  allAdmissions,
  doctorsById,
  departmentsById,
  periodStart,
  assignmentsByAdmissionId = {},
}) {
  return (rows || []).map((row) => {
    const patientId = row.patientId;
    const assignment = getIpdAssignmentForRow(row, assignmentsByAdmissionId);
    const departmentFields = getIpdDepartmentFromSources({ row, assignment });
    const department = resolveDepartmentName({
      ...departmentFields,
      doctorsById,
      doctorId: assignment?.doctorId || row.doctorId,
      departmentsById,
    });
    const isReturning = hasPriorIpdAdmission(patientId, periodStart, allAdmissions);

    return { patientId, department, isReturning };
  });
}

export function formatPeriodLabel(fromDate, toDate) {
  if (fromDate && toDate) return `${fromDate} to ${toDate}`;
  if (fromDate) return `from ${fromDate}`;
  if (toDate) return `until ${toDate}`;
  return "All time";
}

export function formatMonthYearLabel(monthNames, month, year) {
  return `${monthNames[month]} ${year}`;
}

export function buildCsvSummaryLines({
  title,
  periodLabel,
  summary,
}) {
  const lines = [
    title,
    `Period,${periodLabel}`,
    `Total Patients,${summary.total}`,
    `New Patients,${summary.new}`,
    `Returning Patients,${summary.returning}`,
    "",
    "Department,Total Patients,New Patients,Returning Patients",
  ];

  (summary.byDepartment || []).forEach((dept) => {
    lines.push(`${dept.department},${dept.total},${dept.new},${dept.returning}`);
  });

  lines.push("");
  return lines;
}

/**
 * patientId@visitDateKey → appointment for doctor/department display only.
 * Includes cancelled/rejected visits (footfall builders still exclude them).
 * Prefers a non-cancelled same-day appointment when both exist.
 */
export function buildAppointmentDoctorLookupByPatientDate(
  groupedAppointments,
  periodStart = null,
  periodEnd = null,
  periodStartKey = null,
  periodEndKey = null
) {
  /** @type {Map<string, object>} */
  const lookup = new Map();

  Object.entries(groupedAppointments || {}).forEach(([groupKey, appointments]) => {
    if (!Array.isArray(appointments)) return;

    appointments.forEach((apt) => {
      if (!appointmentHasDoctorFields(apt)) return;

      const aptDate = getAppointmentDate(apt);
      if (
        !isVisitDateInPeriod(
          aptDate,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        )
      ) {
        return;
      }

      const visitDateKey = toPeriodDateKey(aptDate);
      if (!visitDateKey) return;

      const patientId = apt?.patientId ?? apt?.patient_id ?? (groupKey || null);
      if (patientId == null || patientId === "") return;

      const key = `${String(patientId)}@${visitDateKey}`;
      const next = { ...apt, patientId, visitDateKey };
      const existing = lookup.get(key);
      if (!existing) {
        lookup.set(key, next);
        return;
      }

      const existingCancelled = isOpdAppointmentCancelledOrRejected(existing);
      const nextCancelled = isOpdAppointmentCancelledOrRejected(apt);
      if (existingCancelled && !nextCancelled) {
        lookup.set(key, next);
        return;
      }
      if (!existingCancelled && nextCancelled) return;

      const existingTime = getAppointmentDate(existing)?.getTime() || 0;
      const nextTime = aptDate?.getTime() || 0;
      if (nextTime >= existingTime) {
        lookup.set(key, next);
      }
    });
  });

  return lookup;
}

/**
 * Fill missing doctorId/doctorName on export rows from cancelled (or active) appointments.
 * Does not add new patient-day rows — display only.
 */
export function applyAppointmentDoctorLookupToRows(
  rows = [],
  doctorLookupByPatientDate = null
) {
  if (!doctorLookupByPatientDate || doctorLookupByPatientDate.size === 0) {
    return rows || [];
  }

  return (rows || []).map((row) => {
    const hasDoctor =
      (row?.doctorId != null &&
        row?.doctorId !== "" &&
        Number(row.doctorId) !== 0) ||
      (row?.doctor_id != null &&
        row?.doctor_id !== "" &&
        Number(row.doctor_id) !== 0) ||
      Boolean(row?.doctorName && String(row.doctorName).trim()) ||
      Boolean(row?.doctor_name && String(row.doctor_name).trim());
    if (hasDoctor) return row;

    const patientId = row?.patientId ?? row?.patient_id;
    const visitDateKey =
      row?.visitDateKey || toPeriodDateKey(getAppointmentDate(row)) || "";
    if (patientId == null || patientId === "" || !visitDateKey) return row;

    const apt = doctorLookupByPatientDate.get(
      `${String(patientId)}@${visitDateKey}`
    );
    if (!apt) return row;

    return {
      ...row,
      doctorId: apt.doctorId ?? apt.doctor_id ?? row.doctorId ?? row.doctor_id ?? null,
      doctorName:
        apt.doctorName ||
        apt.doctor_name ||
        row.doctorName ||
        row.doctor_name ||
        null,
    };
  });
}

/**
 * Resolve assigned doctor display name for any export period.
 * Prefers active same-day appointment; falls back to cancelled/rejected with doctor.
 */
export function resolveAssignedDoctorNameFromAppointments({
  patientId,
  visitDateKey = null,
  groupedAppointments = {},
  doctorsById = {},
  fallbackRow = null,
}) {
  const fromFallbackName =
    fallbackRow?.doctorName || fallbackRow?.doctor_name || null;
  if (fromFallbackName && String(fromFallbackName).trim()) {
    return String(fromFallbackName).trim();
  }

  const fallbackDoctorId = fallbackRow?.doctorId ?? fallbackRow?.doctor_id;
  if (
    fallbackDoctorId != null &&
    fallbackDoctorId !== "" &&
    Number(fallbackDoctorId) !== 0
  ) {
    const doctor =
      doctorsById?.[fallbackDoctorId] ||
      doctorsById?.[String(fallbackDoctorId)] ||
      doctorsById?.[Number(fallbackDoctorId)];
    if (doctor?.name) return String(doctor.name).trim();
  }

  if (patientId == null || patientId === "") return "";

  const appointments = getGroupedAppointmentsForPatient(
    groupedAppointments,
    patientId
  );
  if (!appointments.length) return "";

  const matches = visitDateKey
    ? appointments.filter(
        (apt) => toPeriodDateKey(getAppointmentDate(apt)) === visitDateKey
      )
    : appointments.slice();

  const pickName = (apt) => {
    if (!apt) return "";
    const name = apt.doctorName || apt.doctor_name;
    if (name && String(name).trim()) return String(name).trim();
    const doctorId = apt.doctorId ?? apt.doctor_id;
    if (doctorId == null || doctorId === "" || Number(doctorId) === 0) return "";
    const doctor =
      doctorsById?.[doctorId] ||
      doctorsById?.[String(doctorId)] ||
      doctorsById?.[Number(doctorId)];
    return doctor?.name ? String(doctor.name).trim() : "";
  };

  const active = matches.find((apt) => !isOpdAppointmentCancelledOrRejected(apt));
  const fromActive = pickName(active);
  if (fromActive) return fromActive;

  for (const apt of matches) {
    const name = pickName(apt);
    if (name) return name;
  }

  return "";
}

/**
 * One row per patient per calendar visit day in the period.
 * Monthly Total Patients = sum of daily unique attendees (footfall), not unique patients.
 */
export function buildOpdPatientDayRowsInPeriod(
  groupedAppointments,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  /** @type {Map<string, object>} */
  const dayRows = new Map();

  Object.entries(groupedAppointments || {}).forEach(([groupKey, appointments]) => {
    if (!Array.isArray(appointments)) return;

    appointments.forEach((apt) => {
      // Cancelled / rejected appointments must not inflate monthly footfall.
      if (isOpdAppointmentCancelledOrRejected(apt)) {
        return;
      }

      const aptDate = getAppointmentDate(apt);
      if (
        !isVisitDateInPeriod(
          aptDate,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        )
      ) {
        return;
      }

      const visitDateKey = toPeriodDateKey(aptDate);
      if (!visitDateKey) return;

      const patientId = apt?.patientId ?? apt?.patient_id ?? (groupKey || null);
      if (patientId == null || patientId === "") return;

      const dayKey = `${String(patientId)}@${visitDateKey}`;
      const existing = dayRows.get(dayKey);
      if (!existing) {
        dayRows.set(dayKey, {
          ...apt,
          patientId,
          visitDateKey,
        });
        return;
      }

      const existingTime = getAppointmentDate(existing)?.getTime() || 0;
      const nextTime = aptDate?.getTime() || 0;
      if (nextTime >= existingTime) {
        dayRows.set(dayKey, {
          ...apt,
          patientId,
          visitDateKey,
        });
      }
    });
  });

  return Array.from(dayRows.values());
}

export function buildOpdPatientRowsInPeriod(
  groupedAppointments,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  const visitInPeriodPatientIds = buildOpdVisitInPeriodPatientSet(
    groupedAppointments,
    periodStart,
    periodEnd,
    periodStartKey,
    periodEndKey
  );

  if (!visitInPeriodPatientIds || visitInPeriodPatientIds.size === 0) {
    return [];
  }

  return Array.from(visitInPeriodPatientIds).map((patientId) => {
    const representativeAppointment = getRepresentativeOpdAppointmentInPeriod(
      { patientId },
      groupedAppointments,
      periodStart,
      periodEnd,
      periodStartKey,
      periodEndKey
    );

    return {
      ...representativeAppointment,
      patientId: representativeAppointment?.patientId ?? representativeAppointment?.patient_id ?? patientId,
    };
  });
}

export function filterOpdAppointmentsInPeriod(
  groupedAppointments,
  periodStart,
  periodEnd,
  periodStartKey = null,
  periodEndKey = null
) {
  const rows = [];
  Object.values(groupedAppointments || {}).forEach((appointments) => {
    if (!Array.isArray(appointments)) return;
    appointments.forEach((apt) => {
      const aptDate = getAppointmentDate(apt);
      if (
        !isVisitDateInPeriod(
          aptDate,
          periodStart,
          periodEnd,
          periodStartKey,
          periodEndKey
        )
      ) {
        return;
      }
      rows.push(apt);
    });
  });
  return rows;
}

export function filterIpdAdmissionsInPeriod(admissions, periodStart, periodEnd) {
  return (admissions || []).filter((admission) => {
    const admissionDate = getAdmissionDate(admission);
    return isDateInRange(admissionDate, periodStart, periodEnd);
  });
}
